// Instrument sprites (§4.11): one audio buffer per sound id plus a JSON map
// from sampled MIDI note to its slot in the buffer. Notes are sampled every
// SAMPLE_STEP semitones; notes in between are pitch-shifted by at most ±1
// semitone through `playbackRate`.
//
// Everything in this file is pure (no Web Audio) so it can be unit-tested.

/** Float32 PCM backed by a plain ArrayBuffer (what Web Audio accepts). */
export type Pcm = Float32Array<ArrayBuffer>;

/**
 * `[offsetSec, durSec]`, or `[offsetSec, durSec, loopStartSec, loopEndSec]`
 * for sustained instruments (loop points are relative to the slot start).
 */
export type SpriteEntry = [number, number] | [number, number, number, number];

/** JSON shape of `/sounds/<id>.json`: `{ "40": [0, 2.5], "43": [2.55, 2.5], … }`. */
export type SpriteMap = Record<string, SpriteEntry>;

export const SAMPLE_STEP = 3;

/** Silence between sprite slots, so a slightly late stop never bleeds into the next note. */
export const SPRITE_GAP_SEC = 0.05;

/**
 * Sampled MIDI notes for a range: lo, lo+step, … such that every note in
 * [lo, hi] is within ⌊step/2⌋ semitones of a sample.
 */
export function sampleMidis(lo: number, hi: number, step = SAMPLE_STEP): number[] {
  const out: number[] = [];
  if (hi < lo) [lo, hi] = [hi, lo];
  const reach = Math.floor(step / 2);
  for (let m = lo; ; m += step) {
    out.push(m);
    if (m + reach >= hi) break;
  }
  return out;
}

export interface SampleChoice {
  /** Sampled MIDI note used. */
  key: number;
  /** Slot start in the buffer (seconds). */
  offset: number;
  /** Slot length (seconds). */
  dur: number;
  /** Absolute loop points in the buffer (seconds), for sustained sounds. */
  loopStart?: number;
  loopEnd?: number;
  /** Semitones to shift the sample by. */
  semis: number;
  /** playbackRate for the shift (2^(semis/12)). */
  rate: number;
}

const keyCache = new WeakMap<SpriteMap, number[]>();

/** Sorted numeric keys of a sprite map (cached per map object). */
export function spriteKeys(map: SpriteMap): number[] {
  let keys = keyCache.get(map);
  if (!keys) {
    keys = Object.keys(map)
      .map(Number)
      .filter((k) => Number.isFinite(k))
      .sort((a, b) => a - b);
    keyCache.set(map, keys);
  }
  return keys;
}

export function semitoneRate(semis: number): number {
  return Math.pow(2, semis / 12);
}

/**
 * The sample to play for a MIDI note: the nearest sampled key. On a tie the
 * higher key wins (shifting a sample down sounds better than up). With
 * `exact` (drum kits) the nearest key is played unshifted.
 */
export function nearestSample(map: SpriteMap, midi: number, exact = false): SampleChoice | null {
  const keys = spriteKeys(map);
  if (keys.length === 0) return null;
  // Binary search for the first key ≥ midi.
  let lo = 0;
  let hi = keys.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (keys[mid] < midi) lo = mid + 1;
    else hi = mid;
  }
  let key: number;
  if (lo >= keys.length) key = keys[keys.length - 1];
  else if (lo === 0) key = keys[0];
  else {
    const above = keys[lo];
    const below = keys[lo - 1];
    key = above - midi <= midi - below ? above : below;
  }
  const e = map[String(key)];
  const semis = exact ? 0 : midi - key;
  const choice: SampleChoice = { key, offset: e[0], dur: e[1], semis, rate: semitoneRate(semis) };
  if (e.length === 4) {
    choice.loopStart = e[0] + e[2];
    choice.loopEnd = e[0] + e[3];
  }
  return choice;
}

export interface RenderedNote {
  key: number;
  data: Pcm;
  /** Loop points in samples, relative to the note start. */
  loopStart?: number;
  loopEnd?: number;
}

export interface SpriteLayoutItem {
  key: number;
  /** Sample offset of the slot in the sprite buffer. */
  start: number;
  length: number;
  loopStart?: number;
  loopEnd?: number;
}

/** Slot positions (in samples) for notes of the given lengths, separated by a gap. */
export function layoutSprite(
  notes: readonly { key: number; length: number; loopStart?: number; loopEnd?: number }[],
  sampleRate: number,
  gapSec = SPRITE_GAP_SEC,
): { items: SpriteLayoutItem[]; total: number } {
  const gap = Math.round(gapSec * sampleRate);
  let pos = 0;
  const items: SpriteLayoutItem[] = [];
  for (const n of notes) {
    items.push({ key: n.key, start: pos, length: n.length, loopStart: n.loopStart, loopEnd: n.loopEnd });
    pos += n.length + gap;
  }
  return { items, total: Math.max(1, pos) };
}

/** Map JSON for a layout. Times are exact multiples of 1/sampleRate. */
export function spriteMapOf(items: readonly SpriteLayoutItem[], sampleRate: number): SpriteMap {
  const map: SpriteMap = {};
  for (const it of items) {
    const off = it.start / sampleRate;
    const dur = it.length / sampleRate;
    map[String(it.key)] =
      it.loopStart !== undefined && it.loopEnd !== undefined
        ? [off, dur, it.loopStart / sampleRate, it.loopEnd / sampleRate]
        : [off, dur];
  }
  return map;
}

export interface SpriteData {
  sampleRate: number;
  /** Mono PCM of the whole sprite. */
  data: Pcm;
  map: SpriteMap;
  /** Drum kits: play the nearest key without pitch shifting. */
  exact?: boolean;
}

/** Concatenate rendered notes into one sprite buffer + map. */
export function buildSprite(notes: readonly RenderedNote[], sampleRate: number, exact = false, gapSec = SPRITE_GAP_SEC): SpriteData {
  const { items, total } = layoutSprite(
    notes.map((n) => ({ key: n.key, length: n.data.length, loopStart: n.loopStart, loopEnd: n.loopEnd })),
    sampleRate,
    gapSec,
  );
  const data = new Float32Array(total);
  items.forEach((it, i) => data.set(notes[i].data, it.start));
  return { sampleRate, data, map: spriteMapOf(items, sampleRate), exact: exact || undefined };
}

/** Validate a sprite map loaded from JSON. Returns null when malformed. */
export function parseSpriteMap(json: unknown): SpriteMap | null {
  if (!json || typeof json !== 'object' || Array.isArray(json)) return null;
  const src = json as Record<string, unknown>;
  const out: SpriteMap = {};
  let n = 0;
  for (const [k, v] of Object.entries(src)) {
    if (!Number.isFinite(Number(k)) || !Array.isArray(v)) return null;
    if ((v.length !== 2 && v.length !== 4) || !v.every((x) => typeof x === 'number' && Number.isFinite(x) && x >= 0)) return null;
    out[String(Number(k))] = v.slice() as SpriteEntry;
    n++;
  }
  return n > 0 ? out : null;
}

/** Harmonic number of a natural harmonic touched at a fret (0 when the fret has none). */
export function naturalHarmonicNumber(fret: number): number {
  const TABLE: Record<number, number> = { 12: 2, 24: 2, 7: 3, 19: 3, 5: 4, 4: 5, 9: 5, 16: 5, 3: 6, 15: 6 };
  return TABLE[fret] ?? 0;
}

/**
 * Sounding pitch of a harmonic: natural harmonics sound the touched partial
 * of the open (capo) string, artificial harmonics an octave above the
 * fretted note.
 */
export function harmonicMidi(midi: number, kind: 'natural' | 'artificial', fret?: number): number {
  if (kind === 'natural' && fret !== undefined && fret > 0) {
    const h = naturalHarmonicNumber(fret);
    if (h > 0) return Math.round(midi - fret + 12 * Math.log2(h));
  }
  return midi + 12;
}
